import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { apiErrorMessage } from '../reports/edit/editApi';

/** Базовый адрес журнала действий (все запросы проверяются сервером по сессии и правам роли) */
export const AUDIT_API = '/api/v2/audit';

export type AuditResult = 'success' | 'error' | 'denied';

/** Запись в списке журнала. Сводка изменений (summary) приходит только при праве audit.details.view */
export interface AuditRow {
  id: number;
  createdAt: string;
  userId: number | null;
  username: string | null;
  fullName: string | null;
  role: string | null;
  roleName: string | null;
  roleLevel: number | null;
  actorUserId: number | null;
  actorUsername: string | null;
  module: string;
  moduleTitle: string;
  section: string | null;
  sectionTitle: string | null;
  action: string;
  actionTitle: string;
  kind: string;
  kindTitle: string;
  result: AuditResult;
  resultTitle: string;
  entityType: string | null;
  entityId: string | null;
  entityLabel: string | null;
  pharmacyNum: string | null;
  pharmacyUsername: string | null;
  period: string | null;
  message: string | null;
  summary: string | null;
}

/** Изменение поля «было → стало» */
export interface AuditChange {
  field: string;
  label: string;
  from: unknown;
  to: unknown;
}

/** Содержимое details_json: все блоки необязательны */
export interface AuditDetails {
  changes?: AuditChange[];
  changesTotal?: number;
  snapshot?: unknown;
  ids?: unknown[];
  total?: number;
  params?: unknown;
  truncated?: boolean;
  note?: string;
  [key: string]: unknown;
}

export interface AuditRecord extends AuditRow {
  details: AuditDetails | null;
  ip: string | null;
  userAgent: string | null;
  requestId: string | null;
  httpMethod: string | null;
  httpPath: string | null;
  durationMs: number | null;
}

export interface AuditListResponse {
  rows: AuditRow[];
  total: number;
  page: number;
  pageSize: number;
  pages: number;
}

export interface AuditDetailResponse {
  record: AuditRecord;
  related: AuditRow[];
}

export interface AuditOption {
  id: string;
  title: string;
}

export interface AuditActionOption {
  code: string;
  module: string;
  title: string;
}

export interface AuditFilterOptions {
  modules: AuditOption[];
  actions: AuditActionOption[];
  kinds: AuditOption[];
  results: AuditOption[];
  retentionDays: number;
}

/** Фильтры экрана (пустая строка - условие не задано) */
export interface AuditFilters {
  from: string;
  to: string;
  module: string;
  action: string;
  kind: string;
  result: string;
  user: string;
  pharmacy: string;
  q: string;
}

export interface CleanupPreview {
  expired: number;
  retentionDays: number;
}

interface ApiEnvelope {
  success?: boolean;
  message?: string;
}

type FilterOptionsDto = ApiEnvelope & Partial<AuditFilterOptions>;
type ListDto = ApiEnvelope & Partial<AuditListResponse>;
type DetailDto = ApiEnvelope & Partial<AuditDetailResponse>;
type CleanupPreviewDto = ApiEnvelope & Partial<CleanupPreview>;
type CleanupDto = ApiEnvelope & { deleted?: number };

/** Текст ошибки журнала для пользователя (сообщение сервера или запасной текст) */
export function auditErrorMessage(error: unknown, fallback: string): string {
  return apiErrorMessage(error, fallback);
}

/** Пустые условия не отправляются: сервер сам разбирает только заданные */
function toParams(filters: AuditFilters): Record<string, string> {
  const params: Record<string, string> = {};
  (Object.keys(filters) as Array<keyof AuditFilters>).forEach((name) => {
    const value = filters[name].trim();
    if (value) params[name] = value;
  });
  return params;
}

function ensureSuccess<T extends ApiEnvelope>(data: T | undefined, fallback: string): T {
  if (!data || !data.success) throw new Error((data && data.message) || fallback);
  return data;
}

export async function fetchAuditFilterOptions(): Promise<AuditFilterOptions> {
  const res = await axios.get<FilterOptionsDto>(`${AUDIT_API}/filters`);
  const data = ensureSuccess(res.data, 'Не удалось загрузить справочники журнала');
  return {
    modules: data.modules || [],
    actions: data.actions || [],
    kinds: data.kinds || [],
    results: data.results || [],
    retentionDays: data.retentionDays || 0
  };
}

export async function fetchAuditList(
  filters: AuditFilters,
  page: number,
  pageSize: number
): Promise<AuditListResponse> {
  const res = await axios.get<ListDto>(AUDIT_API, { params: { ...toParams(filters), page, pageSize } });
  const data = ensureSuccess(res.data, 'Не удалось загрузить журнал');
  return {
    rows: data.rows || [],
    total: data.total || 0,
    page: data.page || page,
    pageSize: data.pageSize || pageSize,
    pages: data.pages || 1
  };
}

export async function fetchAuditRecord(id: number): Promise<AuditDetailResponse> {
  const res = await axios.get<DetailDto>(`${AUDIT_API}/${id}`);
  const data = ensureSuccess(res.data, 'Не удалось загрузить запись');
  if (!data.record) throw new Error('Запись журнала не найдена');
  return { record: data.record, related: data.related || [] };
}

/** Запись журнала с подробностями; при смене номера прежний ответ отбрасывается (защита от гонок запросов) */
export function useAuditRecord(id: number | null) {
  const [data, setData] = useState<AuditDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    setData(null);
    setError('');
    if (id === null) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    fetchAuditRecord(id)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) setError(auditErrorMessage(err, 'Не удалось загрузить запись'));
      })
      .then(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, reloadKey]);

  const reload = useCallback(() => setReloadKey((key) => key + 1), []);
  return { data, loading, error, reload };
}

/** Ошибка при ответе-файле приходит блобом: достаем из него сообщение сервера */
async function blobErrorMessage(error: unknown, fallback: string): Promise<string> {
  const response = (error as { response?: { data?: unknown } }).response;
  if (response && response.data instanceof Blob) {
    try {
      const body = JSON.parse(await response.data.text()) as ApiEnvelope;
      if (body.message) return body.message;
    } catch {
      return fallback;
    }
  }
  return auditErrorMessage(error, fallback);
}

/** CSV по текущим фильтрам (без страниц); сам факт выгрузки записывается в журнал сервером */
export async function fetchAuditCsv(filters: AuditFilters): Promise<Blob> {
  try {
    const res = await axios.get<Blob>(`${AUDIT_API}/export`, { params: toParams(filters), responseType: 'blob' });
    return res.data;
  } catch (err) {
    throw new Error(await blobErrorMessage(err, 'Не удалось выгрузить журнал'));
  }
}

export async function fetchCleanupPreview(): Promise<CleanupPreview> {
  const res = await axios.get<CleanupPreviewDto>(`${AUDIT_API}/cleanup`);
  const data = ensureSuccess(res.data, 'Не удалось получить объем очистки');
  return { expired: data.expired || 0, retentionDays: data.retentionDays || 0 };
}

/** Удаляет только записи старше срока хранения; возвращает число удаленных */
export async function runAuditCleanup(): Promise<number> {
  const res = await axios.delete<CleanupDto>(`${AUDIT_API}/cleanup`);
  return ensureSuccess(res.data, 'Не удалось выполнить очистку').deleted || 0;
}
